import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { pushEvent } from "../lib/analytics";
import { CheckCircle2, ArrowRight, FileText, Plane } from "lucide-react";

export default function ThankYou() {
  const [params] = useSearchParams();
  const name = params.get("name") || "";
  const email = params.get("email") || "";
  const first = name.trim().split(" ")[0] || "pilot";

  useEffect(() => {
    pushEvent("lead_thank_you_view", { has_email: !!email });
    if (typeof window.fbq === "function") {
      window.fbq("track", "ViewContent", { content_name: "Lead Thank You" });
    }
  }, [email]);

  const registerHref = `/register?name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}`;

  return (
    <div className="min-h-screen pb-16" data-testid="thank-you-page">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="p-8 md:p-10 rounded-sm border border-primary/40 bg-primary/5"
        >
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-primary mb-4">
            <Plane className="h-4 w-4" /> Step 2 of 2
          </span>

          <div className="flex items-start gap-3 mb-3">
            <CheckCircle2 className="h-8 w-8 text-primary shrink-0 mt-1" />
            <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight" data-testid="thank-you-heading">
              You're almost there, {first}!
            </h1>
          </div>

          <p className="text-base text-slate-300 leading-relaxed mb-2">
            You've taken the first step toward learning to fly drones like a certified pilot with DronAcademy.
          </p>
          <p className="text-base text-slate-300 leading-relaxed mb-7">
            Your next step takes less than a minute. Create your free account to unlock Mission One and start flying —
            no card required.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to={registerHref} className="flex-1" data-testid="thank-you-register-cta">
              <Button className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-semibold text-base">
                Create My Free Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="/part-107-checklist" target="_blank" rel="noopener noreferrer" data-testid="thank-you-checklist-cta">
              <Button variant="outline" className="h-12 px-6 rounded-sm border-primary/50 text-primary hover:bg-primary/10 w-full">
                <FileText className="mr-2 h-4 w-4" /> Open my free checklist
              </Button>
            </a>
          </div>

          <p className="text-xs text-muted-foreground mt-6">
            We also emailed your free FAA Part 107 checklist to {email || "your inbox"}. Check your Promotions tab if you don't see it.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
